import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, CircularProgress, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
} from '@mui/material';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Page, Card, StatCard } from '../ui';

const PERIODS = [
  { label: 'Сегодня', value: 'today' }, { label: 'Вчера', value: 'yesterday' },
  { label: '7 дней', value: '7d' }, { label: '30 дней', value: '30d' },
];
const isoLocal = (d) => {
  const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return x.toISOString().slice(0, 10);
};
function rangeFor(period) {
  const t = new Date();
  const today = isoLocal(t);
  if (period === 'today') return { from: today, to: today };
  if (period === 'yesterday') { const y = new Date(t); y.setDate(t.getDate() - 1); return { from: isoLocal(y), to: isoLocal(y) }; }
  const n = period === '7d' ? 6 : 29;
  const s = new Date(t); s.setDate(t.getDate() - n);
  return { from: isoLocal(s), to: today };
}
const dateInputSx = (val) => ({
  colorScheme: 'dark', bgcolor: 'transparent', border: 'none', outline: 'none',
  color: val ? 'text.primary' : 'text.disabled', fontSize: 13, fontFamily: 'inherit', width: 112,
  '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.6)', cursor: 'pointer' },
});

const money = (n) => '$' + Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const dateShort = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch (e) { return '—'; }
};

export default function AdminPage() {
  const [data, setData] = useState({ users: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const rangeMode = Boolean(rangeFrom && rangeTo);
  const { from, to } = rangeMode ? { from: rangeFrom, to: rangeTo } : rangeFor(period);

  // диалог «создать доступ»
  const [createOpen, setCreateOpen] = useState(false);
  const [newLogin, setNewLogin] = useState('');
  const [newPass, setNewPass] = useState('');
  const [createErr, setCreateErr] = useState('');
  const [creating, setCreating] = useState(false);

  // диалог «сбросить пароль»
  const [pwdTarget, setPwdTarget] = useState(null);   // { id, username }
  const [pwdValue, setPwdValue] = useState('');
  const [pwdErr, setPwdErr] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/admin/overview', { params: { from, to } }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const createUser = () => {
    setCreating(true); setCreateErr('');
    axios.post('/admin/users', { username: newLogin.trim(), password: newPass })
      .then(() => { setCreateOpen(false); setNewLogin(''); setNewPass(''); load(); })
      .catch((e) => setCreateErr((e.response && e.response.data && e.response.data.detail) || 'Не удалось создать'))
      .finally(() => setCreating(false));
  };

  const resetPassword = () => {
    setPwdBusy(true); setPwdErr('');
    axios.post(`/admin/users/${pwdTarget.id}/password`, { password: pwdValue })
      .then(() => { setPwdTarget(null); setPwdValue(''); })
      .catch((e) => setPwdErr((e.response && e.response.data && e.response.data.detail) || 'Не удалось'))
      .finally(() => setPwdBusy(false));
  };

  const removeUser = (u) => {
    if (!window.confirm(`Удалить доступ «${u.username}»? Все его аккаунты, статистика и данные будут удалены безвозвратно.`)) return;
    axios.delete(`/admin/users/${u.id}`).then(load).catch((e) => {
      window.alert((e.response && e.response.data && e.response.data.detail) || 'Не удалось удалить');
    });
  };

  const t = data.totals || {};
  const headSx = { fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' };

  return (
    <Page
      title="Команда"
      subtitle="Сводка по всем пользователям сервиса"
      action={<Button variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => { setCreateErr(''); setCreateOpen(true); }}>Создать доступ</Button>}
    >
      {/* Период (та же логика, что в «Статистике») */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {PERIODS.map((p) => (
          <Button key={p.value} onClick={() => { setRangeFrom(''); setRangeTo(''); setPeriod(p.value); }}
            variant={!rangeMode && period === p.value ? 'contained' : 'text'}
            sx={{ minWidth: 0, px: 2, color: !rangeMode && period === p.value ? undefined : 'text.secondary' }}>
            {p.label}
          </Button>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.25, ml: 0.5, borderLeft: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Период</Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, height: 36, borderRadius: '8px',
            border: '1px solid', borderColor: rangeMode ? 'primary.main' : 'divider', bgcolor: 'background.paper',
          }}>
            <Box component="input" type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} sx={dateInputSx(rangeFrom)} />
            <Box sx={{ color: 'text.disabled', fontSize: 13 }}>–</Box>
            <Box component="input" type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} sx={dateInputSx(rangeTo)} />
          </Box>
          {rangeMode && <Button size="small" onClick={() => { setRangeFrom(''); setRangeTo(''); }} sx={{ minWidth: 0, px: 1, color: 'text.secondary' }}>Сбросить</Button>}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <StatCard icon={<PeopleAltRoundedIcon />} loading={loading} value={t.users ?? 0} label="Пользователей" />
        <StatCard icon={<AccountCircleRoundedIcon />} loading={loading} value={t.active_accounts ?? 0} label="Активных аккаунтов" sub={`из ${t.accounts ?? 0} всего`} />
        <StatCard icon={<PaymentsRoundedIcon />} loading={loading} value={money(t.spend_period)} label="Расход за период" />
        <StatCard icon={<SavingsRoundedIcon />} loading={loading} value={money(t.spend_all)} label="Расход за всё время" />
      </Box>

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Пользователь</TableCell>
              <TableCell sx={headSx} align="center">Активные / всего</TableCell>
              <TableCell sx={headSx} align="center">Кабинеты</TableCell>
              <TableCell sx={headSx} align="right">Расход за период</TableCell>
              <TableCell sx={headSx} align="right">Расход всего</TableCell>
              <TableCell sx={headSx} align="right">Добавлен</TableCell>
              <TableCell sx={headSx} align="right">Доступ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, border: 'none' }}><CircularProgress size={26} /></TableCell></TableRow>
            ) : data.users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <PeopleAltRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Пользователей пока нет</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Нажми «Создать доступ», чтобы добавить участника команды.</Typography>
                </TableCell>
              </TableRow>
            ) : data.users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{u.username}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: u.active_accounts > 0 ? 'success.main' : 'text.secondary' }}>
                      {u.active_accounts}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>/ {u.accounts}</Typography>
                    {u.unverified > 0 && (
                      <Chip label={`${u.unverified} не пров.`} size="small" color="warning" variant="outlined" sx={{ height: 19, fontSize: 10 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 13, color: 'text.secondary' }}>{u.ad_accounts}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{money(u.spend_period)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: 'text.secondary' }}>{money(u.spend_all)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12.5, color: 'text.secondary' }}>{dateShort(u.last_added)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Сбросить пароль">
                    <IconButton size="small" onClick={() => { setPwdErr(''); setPwdValue(''); setPwdTarget(u); }} sx={{ color: 'text.secondary' }}>
                      <LockResetRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить доступ">
                    <IconButton size="small" onClick={() => removeUser(u)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Создать доступ */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Создать доступ</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            Новый участник войдёт на сайте под этим логином и паролем и получит свою отдельную среду.
          </Typography>
          <TextField label="Логин" fullWidth autoFocus value={newLogin} onChange={(e) => setNewLogin(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="Пароль" fullWidth value={newPass} onChange={(e) => setNewPass(e.target.value)} helperText="Минимум 6 символов" />
          {createErr && <Alert severity="error" sx={{ mt: 2, borderRadius: '6px' }}>{createErr}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={createUser} disabled={creating || !newLogin.trim() || !newPass}>
            {creating ? 'Создание…' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Сбросить пароль */}
      <Dialog open={!!pwdTarget} onClose={() => setPwdTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Новый пароль</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            Пользователь: <b>{pwdTarget && pwdTarget.username}</b>. Задай новый пароль — старый перестанет работать.
          </Typography>
          <TextField label="Новый пароль" fullWidth autoFocus value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} helperText="Минимум 6 символов" />
          {pwdErr && <Alert severity="error" sx={{ mt: 2, borderRadius: '6px' }}>{pwdErr}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwdTarget(null)}>Отмена</Button>
          <Button variant="contained" onClick={resetPassword} disabled={pwdBusy || !pwdValue}>
            {pwdBusy ? 'Сохранение…' : 'Сменить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
